import type {
  HierarchicalRulesetConfigField,
  HierarchicalRulesetMetadataField,
  HierarchyType,
  ModifiedFields,
  PreviewChangesIndividualRuleset,
  PreviewData,
  PreviewDataChildContent,
  TypedField,
  ValueType
} from '@library/types/rulesets';
import {
  isConfigField,
  isMetadataField
} from '@library/component-utils/rulesets';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCreateHierarchicalRulesetMutation,
  useGetAllowedRuleTypesQuery,
  useGetAllowedRulesQuery,
  useGetHierarchicalRulesetQuery,
  useGetHierarchicalRulesetsQuery,
  usePreviewChangesMutation,
  useUpdateHierarchicalRulesetMutation
} from '@bo-services/rulesetsApi';
import { clone } from 'ramda';
import { useImmer } from 'use-immer';

export type CustomRulesetParams = {
  hierarchyType: HierarchyType;
  rootLevelScopeIds: Array<string>;
};

export const useRulesets = ({
  custom = {
    hierarchyType: 'UserGroup',
    rootLevelScopeIds: []
  }
}: {
  custom?: CustomRulesetParams;
}) => {
  const [selectedQualifiedPath, setSelectedQualifiedPath] = useState('');
  const [expandAllPhantoms, setExpandAllPhantoms] = useState(false);

  const isCustom = custom.rootLevelScopeIds.length >= 1;

  const {
    data: gameOpsHierarchicalRulesetsData,
    isLoading: isGameOpsLoading,
    isFetching: isGameOpsFetching
  } = useGetHierarchicalRulesetsQuery(
    {
      hierarchyType: 'GameOps',
      expandAllPhantoms,
      includePhantomRoot: true
    },
    { skip: isCustom }
  );

  const {
    data: gameModesHierarchicalRulesetsData,
    isLoading: isGameModesLoading,
    isFetching: isGameModesFetching
  } = useGetHierarchicalRulesetsQuery(
    {
      hierarchyType: 'GameMode',
      expandAllPhantoms,
      includePhantomRoot: true
    },
    { skip: isCustom }
  );

  const {
    data: customHierarchicalRulesetsData,
    isFetching: isFetchingCustomRulesetRuleset
  } = useGetHierarchicalRulesetsQuery(
    {
      hierarchyType: custom.hierarchyType,
      rootLevelScopeIds: custom.rootLevelScopeIds,
      expandAllPhantoms,
      includePhantomRoot: true
    },
    { skip: !isCustom }
  );

  const { data: currentRuleset, isFetching: isRulesetFetching } =
    useGetHierarchicalRulesetQuery(
      {
        qualified_path: selectedQualifiedPath,
        include_phantom_root: true,
        expand_all_phantoms: true
      },
      {
        skip: !selectedQualifiedPath
      }
    );

  const { data: allowedRulesetsData } = useGetAllowedRulesQuery();

  const { data: allowedRuleTypesData } = useGetAllowedRuleTypesQuery();

  const [
    updateHierarchicalRuleset,
    {
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      isLoading: isUpdateLoading,
      reset: resetUpdate
    }
  ] = useUpdateHierarchicalRulesetMutation();

  const [
    createHierarchicalRuleset,
    {
      isSuccess: isCreateSuccess,
      isError: isCreateError,
      isLoading: isCreateLoading,
      reset: resetCreate
    }
  ] = useCreateHierarchicalRulesetMutation();

  const isSubmitSuccess = isUpdateSuccess || isCreateSuccess;
  const isSubmitError = isUpdateError || isCreateError;
  const isSubmitLoading = isUpdateLoading || isCreateLoading;

  const [previewChanges, { isLoading: isPreviewLoading, reset: resetPreview }] =
    usePreviewChangesMutation();

  const [modifiedFields, setModifiedFields] = useImmer<ModifiedFields>({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);

  // discard changes if switching to a different ruleset
  useEffect(() => {
    setModifiedFields(() => ({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQualifiedPath]);

  const gameOpsGlobalRuleset = useMemo(
    () =>
      gameOpsHierarchicalRulesetsData?.data.find(
        (ruleset) => ruleset.attributes.level_scope_id === 0
      ),
    [gameOpsHierarchicalRulesetsData?.data]
  );

  const gameModesGlobalRulesets = useMemo(
    () => gameModesHierarchicalRulesetsData?.data,
    [gameModesHierarchicalRulesetsData?.data]
  );

  const customRuleset = useMemo(
    () => customHierarchicalRulesetsData?.data?.[0],
    [customHierarchicalRulesetsData?.data]
  );

  const allowedRules = useMemo(
    () => allowedRulesetsData?.data.attributes.allowed_rules,
    [allowedRulesetsData]
  );

  const allowedRuleTypes = useMemo(
    () => allowedRuleTypesData?.data.attributes.allowed_rule_types,
    [allowedRuleTypesData]
  );

  const matchedAllowedRules = useMemo(() => {
    if (!allowedRules) return new Set([]);

    if (currentRuleset) {
      return new Set(
        allowedRules[currentRuleset.attributes.hierarchy_type][
          currentRuleset.attributes.level_scope_type
        ]
      );
    }

    return new Set([]);
  }, [allowedRules, currentRuleset]);

  const matchedAllowedRuleTypes = useMemo(() => {
    if (!allowedRuleTypes || !currentRuleset)
      return {} as Record<HierarchicalRulesetConfigField, Array<ValueType>>;

    return allowedRuleTypes[currentRuleset.attributes.hierarchy_type][
      currentRuleset.attributes.level_scope_type
    ];
  }, [allowedRuleTypes, currentRuleset]);

  const handleResetPreview = useCallback(() => {
    resetPreview();
    setPreviewData(null);
  }, [resetPreview]);

  const handleOpenPreviewModal = useCallback(() => {
    setIsPreviewModalOpen(true);
  }, [setIsPreviewModalOpen]);

  const handleClosePreviewModal = useCallback(() => {
    setIsPreviewModalOpen(false);
    handleResetPreview();
  }, [handleResetPreview]);

  // only discard unsaved changes from current editing session
  const handleDiscardChanges = useCallback(() => {
    setModifiedFields(() => ({}));
  }, [setModifiedFields]);

  // completely blow away all field values; default this ruleset to inheriting all values from parent;
  // fields that aren't actually present at this level shouldn't matter
  const handleResetRuleset = useCallback(() => {
    setModifiedFields(() => ({
      active: true,
      max_lineup_amount: {
        type: matchedAllowedRuleTypes.max_lineup_amount?.[0] || 'literal',
        value: ''
      },
      max_amount_won: {
        type: matchedAllowedRuleTypes.max_amount_won?.[0] || 'literal',
        value: ''
      },
      max_daily_lineup_amount: {
        type: matchedAllowedRuleTypes.max_daily_lineup_amount?.[0] || 'literal',
        value: ''
      },
      max_liability_per_pick: {
        type: matchedAllowedRuleTypes.max_liability_per_pick?.[0] || 'literal',
        value: ''
      },
      flash_promo_max: {
        type: matchedAllowedRuleTypes.flash_promo_max?.[0] || 'literal',
        value: ''
      },
      min_lineup_amount: {
        type: matchedAllowedRuleTypes.min_lineup_amount?.[0] || 'literal',
        value: ''
      },
      in_game_multiplier: {
        type: matchedAllowedRuleTypes.in_game_multiplier?.[0] || 'literal',
        value: ''
      },
      refund_in_minutes: {
        type: matchedAllowedRuleTypes.refund_in_minutes?.[0] || 'literal',
        value: ''
      },
      daily_promo_limit: {
        type: matchedAllowedRuleTypes.daily_promo_limit?.[0] || 'literal',
        value: ''
      },
      allow_tailed_entries: { type: 'boolean', value: undefined },
      allow_two_players_from_same_game: { type: 'boolean', value: undefined },
      allow_two_players_from_same_team: { type: 'boolean', value: undefined },
      allow_deposits: { type: 'boolean', value: undefined },
      allow_withdrawals: { type: 'boolean', value: undefined },
      enforce_at_least_two_players_from_two_different_games: {
        type: 'boolean',
        value: undefined
      },
      enforce_at_least_two_players_from_two_different_teams: {
        type: 'boolean',
        value: undefined
      },
      enforce_single_game_validations: { type: 'boolean', value: undefined }
    }));
  }, [matchedAllowedRuleTypes, setModifiedFields]);

  // edit ruleset properties within current editing session
  const handleUpdateRulesetField = useCallback(
    ({
      fieldName,
      type,
      value
    }: {
      fieldName:
        | HierarchicalRulesetMetadataField
        | HierarchicalRulesetConfigField;
      type?: ValueType;
      value: string | number | boolean;
    }) => {
      if (isMetadataField(fieldName)) {
        if (String(currentRuleset?.attributes[fieldName]) === String(value)) {
          setModifiedFields((draft) => {
            delete draft[fieldName];
          });
        } else {
          setModifiedFields((draft) => ({
            ...draft,
            [fieldName]: fieldName === 'active' ? Boolean(value) : String(value)
          }));
        }
      } else if (isConfigField(fieldName) && type) {
        if (
          currentRuleset?.attributes[fieldName].type === type &&
          String(currentRuleset?.attributes[fieldName].value) === String(value)
        ) {
          setModifiedFields((draft) => {
            delete draft[fieldName];
          });
        } else {
          let strictFieldType;
          if (type === 'boolean') {
            strictFieldType = Boolean(value);
          } else if (matchedAllowedRuleTypes[fieldName]?.length === 1) {
            // coerce field type if only one type is allowed
            strictFieldType = String(matchedAllowedRuleTypes[fieldName][0]);
          } else {
            strictFieldType = type;
          }
          setModifiedFields((draft) => ({
            ...draft,
            [fieldName]: {
              type: strictFieldType,
              value: type === 'boolean' ? Boolean(value) : String(value)
            }
          }));
        }
      }
    },
    [currentRuleset?.attributes, matchedAllowedRuleTypes, setModifiedFields]
  );

  const configureSubmissionPayload = useCallback(() => {
    const payload: Record<string, unknown> = clone(modifiedFields);
    Object.keys(modifiedFields).forEach((fieldName) => {
      if (isConfigField(fieldName)) {
        if (
          (modifiedFields[fieldName]?.type !== 'boolean' &&
            !modifiedFields[fieldName]!.value) ||
          (modifiedFields[fieldName]?.type === 'boolean' &&
            typeof modifiedFields[fieldName].value !== 'boolean')
        ) {
          payload[fieldName] = {};
        } else {
          payload[fieldName] = {
            type:
              matchedAllowedRuleTypes[fieldName]?.length === 1
                ? String(matchedAllowedRuleTypes[fieldName][0])
                : modifiedFields[fieldName]?.type,
            value:
              modifiedFields[fieldName]?.type === 'boolean'
                ? Boolean(modifiedFields[fieldName].value)
                : Number(modifiedFields[fieldName]!.value)
          };
        }
      }
    });
    return payload;
  }, [matchedAllowedRuleTypes, modifiedFields]);

  // submit and save all current changes
  const handleSubmitUpdate = useCallback(async () => {
    if (!currentRuleset) return;

    const payload = configureSubmissionPayload();

    try {
      if (currentRuleset.id !== undefined && currentRuleset.id !== null) {
        // Existing node: Update
        await updateHierarchicalRuleset({
          id: currentRuleset.id,
          ...payload
        });
      } else {
        await createHierarchicalRuleset({
          ...payload,
          qualified_path: currentRuleset.attributes.qualified_path,
          name: currentRuleset.attributes.name,
          description: currentRuleset.attributes.description,
          active: modifiedFields.active ?? false,
          level_scope_id: currentRuleset.attributes.level_scope_id,
          level_scope_type: currentRuleset.attributes.level_scope_type
        }).unwrap();
      }
      setModifiedFields({});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error updating ruleset: ', error);
    }
  }, [
    configureSubmissionPayload,
    createHierarchicalRuleset,
    currentRuleset,
    modifiedFields.active,
    setModifiedFields,
    updateHierarchicalRuleset
  ]);

  // preview the downstream effects of these changes on other (child) rulesets
  const handlePreviewChanges = useCallback(async () => {
    if (!currentRuleset) return;

    const payload = configureSubmissionPayload();

    try {
      const response = await previewChanges({
        id: currentRuleset.id,
        ...payload
      }).unwrap();
      if (response.attributes) {
        const previewContent: PreviewData = { total: 0, childContent: [] };
        previewContent.total = response.attributes.affected_node_count;

        if (response.attributes.children) {
          const sample = response.attributes.children.data.slice(0, 3);
          previewContent.childContent = sample.map((child) => {
            const childDetails: PreviewDataChildContent = {
              rulesetName: child.attributes.name,
              affectedFields: []
            };
            Object.keys(child.attributes).forEach((key) => {
              const attrKey =
                key as keyof PreviewChangesIndividualRuleset['attributes'];
              if (
                typeof child.attributes[attrKey] === 'object' &&
                child.attributes[attrKey] !== null &&
                'preview_derived' in child.attributes[attrKey] &&
                'derived' in child.attributes[attrKey]
              ) {
                childDetails.affectedFields.push({
                  fieldName: attrKey,
                  previousValue: child.attributes[attrKey]
                    .derived as TypedField['value'],
                  newValue: child.attributes[attrKey]
                    .preview_derived as TypedField['value']
                });
              }
            });
            return childDetails;
          });
        }
        setPreviewData(previewContent);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error previewing ruleset changes: ', error);
    }
  }, [configureSubmissionPayload, currentRuleset, previewChanges]);

  const rulesetsContext = useMemo(
    () => ({
      setExpandAllPhantoms,
      selectedQualifiedPath,
      setSelectedQualifiedPath,
      currentRuleset,
      expandAllPhantoms,
      gameOpsGlobalRuleset,
      gameModesGlobalRulesets,
      modifiedFields,
      handleDiscardChanges,
      handleUpdateRulesetField,
      handleSubmitUpdate,
      handleResetRuleset,
      isSubmitSuccess,
      isSubmitError,
      isSubmitLoading,
      resetUpdate,
      resetCreate,
      isRulesetFetching,
      isGameOpsLoading,
      isGameOpsFetching,
      isGameModesLoading,
      isGameModesFetching,
      allowedRules,
      matchedAllowedRules,
      matchedAllowedRuleTypes,
      handleOpenPreviewModal,
      handleClosePreviewModal,
      isPreviewModalOpen,
      handlePreviewChanges,
      isPreviewLoading,
      previewData,
      handleResetPreview,
      customRuleset,
      isFetchingCustomRulesetRuleset
    }),
    [
      setExpandAllPhantoms,
      selectedQualifiedPath,
      setSelectedQualifiedPath,
      currentRuleset,
      expandAllPhantoms,
      gameOpsGlobalRuleset,
      gameModesGlobalRulesets,
      modifiedFields,
      handleDiscardChanges,
      handleUpdateRulesetField,
      handleSubmitUpdate,
      handleResetRuleset,
      isSubmitSuccess,
      isSubmitError,
      isSubmitLoading,
      resetUpdate,
      resetCreate,
      isRulesetFetching,
      isGameOpsLoading,
      isGameModesLoading,
      isGameOpsFetching,
      isGameModesFetching,
      allowedRules,
      matchedAllowedRules,
      matchedAllowedRuleTypes,
      handleOpenPreviewModal,
      handleClosePreviewModal,
      isPreviewModalOpen,
      handlePreviewChanges,
      isPreviewLoading,
      previewData,
      handleResetPreview,
      customRuleset,
      isFetchingCustomRulesetRuleset
    ]
  );

  return {
    rulesetsContext
  };
};
